/**
 * Service for receiving real-time server-sent events (SSE) from the backend.
 *
 * Opens a persistent EventSource connection to the SSE endpoint, authenticating
 * via the stored JWT token. Consumers subscribe to specific event types (e.g.
 * 'concierge_chat.updated', 'notification.received') through the `on<T>()` method,
 * which returns a typed Observable filtered to that event type.
 *
 * Handles mobile lifecycle: disconnects when the app goes to background and
 * reconnects when it returns to foreground (via Capacitor App plugin).
 *
 * All EventSource callbacks are run inside NgZone so Angular change detection
 * picks up incoming events automatically.
 *
 * Usage:
 *   sseService.connect(['concierge_chat.updated']);
 *   sseService.on<ConciergeChat>('concierge_chat.updated').subscribe(event => { ... });
 *   sseService.disconnect();
 */
import { inject, Injectable, NgZone, signal } from '@angular/core';
import { Observable, Subject, filter, map } from 'rxjs';

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import { environment } from 'src/environments/environment';

export const SSE_EVENT_TYPES = [
  'concierge_chat.updated',
  'notification.received'
] as const;

export type SseConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface SseEvent<T = unknown> {
  type: string;
  id?: string;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class SseService {
  private readonly zone = inject(NgZone);

  readonly connectionState = signal<SseConnectionState>('disconnected');

  private eventSource: EventSource | null = null;
  private eventBus = new Subject<SseEvent>();
  private registeredEventTypes: string[] = [];
  private lifecycleListenerRegistered = false;
  private lastEventId: string | null = null;

  connect(eventTypes?: string[]): void {
    if (this.eventSource) { return; }

    const token = localStorage.getItem('tidy_token');
    if (!token) {
      this.connectionState.set('error');
      return;
    }

    const params = new URLSearchParams();
    params.set('access_token', token);
    if (this.lastEventId) {
      params.set('last_event_id', this.lastEventId);
    }
    if (eventTypes?.length) {
      for (const type of eventTypes) {
        params.append('events[]', type);
      }
    }
    const url = `${environment.api_url}api/v1/customer/sse?${params.toString()}`;

    this.connectionState.set('connecting');
    this.registeredEventTypes = eventTypes ?? [];

    const es = new EventSource(url);

    es.onopen = () => {
      this.zone.run(() => this.connectionState.set('connected'));
    };

    es.onerror = () => {
      this.zone.run(() => this.connectionState.set('error'));
    };

    for (const type of this.registeredEventTypes) {
      es.addEventListener(type, (event: MessageEvent) => {
        this.zone.run(() => {
          try {
            if (event.lastEventId) {
              this.lastEventId = event.lastEventId;
            }
            this.eventBus.next({
              type,
              id: event.lastEventId || undefined,
              data: JSON.parse(event.data),
            });
          } catch (e) {
            console.error(`[SSE] Failed to parse event data for "${type}":`, e);
          }
        });
      });
    }

    this.eventSource = es;
    this.registerLifecycleListener();
  }

  disconnect(): void {
    this.closeEventSource();
    this.registeredEventTypes = [];
    this.lastEventId = null;
    this.connectionState.set('disconnected');
  }

  on<T>(eventType: string): Observable<SseEvent<T>> {
    return this.eventBus.pipe(
      filter(event => event.type === eventType),
      map(event => event as SseEvent<T>),
    );
  }

  private closeEventSource(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /** Disconnects on background, reconnects with the saved event types on foreground. */
  private registerLifecycleListener(): void {
    if (this.lifecycleListenerRegistered || !Capacitor.isNativePlatform()) { return; }
    this.lifecycleListenerRegistered = true;

    App.addListener('appStateChange', ({ isActive }) => {
      this.zone.run(() => {
        if (isActive) {
          this.reconnect();
        } else {
          this.closeEventSource();
          this.connectionState.set('disconnected');
        }
      });
    });
  }

  private reconnect(): void {
    if (this.eventSource) { return; }
    const eventTypes = [...this.registeredEventTypes];
    // Reset so connect() doesn't early-return
    this.registeredEventTypes = [];
    this.connect(eventTypes.length ? eventTypes : undefined);
  }
}
